import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView, PAGE_NAMES } from "../utils/analytics";

/**
 * PageTracker Component
 *
 * Automatically tracks page views when the route changes.
 * Should be included once in the App component to track all route changes.
 */
const PageTracker = () => {
  const location = useLocation();
  const previousPath = useRef(location.pathname);

  useEffect(() => {
    // Only track if the path actually changed
    if (previousPath.current !== location.pathname) {
      // Get page name from pathname
      const pageName = getPageNameFromPath(location.pathname);

      // Track the page view
      trackPageView(pageName, {
        path: location.pathname,
        search: location.search,
        hash: location.hash,
        referrer: previousPath.current,
      });

      // Update previous path
      previousPath.current = location.pathname;
    }
  }, [location]);

  // This component renders nothing
  return null;
};

/**
 * Map pathname to friendly page names
 * @param {string} pathname - The current pathname
 * @returns {string} - Friendly page name
 */
const getPageNameFromPath = (pathname) => {
  // Remove leading slash and split by slash
  const segments = pathname.replace(/^\//, "").split("/");

  // Map common routes to page names
  const routeMap = {
    "/": PAGE_NAMES.HOME,
    "/projects": PAGE_NAMES.PROJECTS,
    "/new-project": PAGE_NAMES.NEW_PROJECT,
    "/deals": PAGE_NAMES.DEALS,
    "/new-deal": PAGE_NAMES.NEW_DEAL,
    "/valuations": PAGE_NAMES.VALUATIONS,
    "/new-valuation": PAGE_NAMES.NEW_VALUATION,
    "/automations": PAGE_NAMES.AUTOMATIONS,

    // Reports routes
    "reports/overview": "Analytics Overview",
    "reports/performance": "Performance Analytics",
    "reports/trends": "Market Trends",

    // Documents routes
    "documents/templates": "Document Templates",
    "documents/reports": "Generated Reports",
    "documents/shared": "Shared Files",

    // User management routes
    users: "User Management",
    "users/roles": "Roles & Permissions",
    "users/teams": "Team Settings",

    // Settings routes
    "settings/general": "General Settings",
    "settings/integrations": "Integrations",
    "settings/security": "Security Settings",
    "settings/backup": "Backup Settings",
  };

  // Check for exact match first
  const exactMatch = routeMap[pathname.replace(/^\//, "")];
  if (exactMatch) {
    return exactMatch;
  }

  // Handle dynamic routes or unknown routes
  if (segments.length >= 2) {
    const [section, id] = segments;
    if (section && id && id !== "new") {
      // This is likely a detail page (e.g., /deals/123)
      const sectionName = section.charAt(0).toUpperCase() + section.slice(1);
      return `${sectionName} Detail`;
    }
  }

  // Fallback: capitalize first segment
  if (segments[0]) {
    return segments[0].charAt(0).toUpperCase() + segments[0].slice(1);
  }

  return "Unknown Page";
};

export default PageTracker;
